/** Axis-aligned bounding box in projected CRS units (X = easting, Y = northing). */
export interface Bbox2 {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
}

/** Axis-aligned bounding box with an elevation range. */
export interface Bbox3 extends Bbox2 {
  minZ: number;
  maxZ: number;
}

export const emptyBbox2 = (): Bbox2 => ({
  minX: Infinity,
  minY: Infinity,
  maxX: -Infinity,
  maxY: -Infinity,
});

export const emptyBbox3 = (): Bbox3 => ({ ...emptyBbox2(), minZ: Infinity, maxZ: -Infinity });

export const isEmptyBbox = (b: Bbox2): boolean => !(b.maxX >= b.minX && b.maxY >= b.minY);

export function growBbox2(b: Bbox2, x: number, y: number): void {
  if (x < b.minX) b.minX = x;
  if (y < b.minY) b.minY = y;
  if (x > b.maxX) b.maxX = x;
  if (y > b.maxY) b.maxY = y;
}

export function growBbox3(b: Bbox3, x: number, y: number, z: number): void {
  growBbox2(b, x, y);
  if (z < b.minZ) b.minZ = z;
  if (z > b.maxZ) b.maxZ = z;
}

export function unionBbox2<T extends Bbox2>(a: T, b: Bbox2): T {
  a.minX = Math.min(a.minX, b.minX);
  a.minY = Math.min(a.minY, b.minY);
  a.maxX = Math.max(a.maxX, b.maxX);
  a.maxY = Math.max(a.maxY, b.maxY);
  return a;
}

export function unionBbox3(a: Bbox3, b: Bbox3): Bbox3 {
  unionBbox2(a, b);
  a.minZ = Math.min(a.minZ, b.minZ);
  a.maxZ = Math.max(a.maxZ, b.maxZ);
  return a;
}

export const bboxWidth = (b: Bbox2): number => b.maxX - b.minX;
export const bboxHeight = (b: Bbox2): number => b.maxY - b.minY;
export const bboxCenterX = (b: Bbox2): number => (b.minX + b.maxX) / 2;
export const bboxCenterY = (b: Bbox2): number => (b.minY + b.maxY) / 2;

export function intersects2(a: Bbox2, b: Bbox2): boolean {
  return a.minX <= b.maxX && a.maxX >= b.minX && a.minY <= b.maxY && a.maxY >= b.minY;
}

/** Clip `a` to `b`; returns null when they do not overlap. */
export function clipBbox2(a: Bbox2, b: Bbox2): Bbox2 | null {
  const out = {
    minX: Math.max(a.minX, b.minX),
    minY: Math.max(a.minY, b.minY),
    maxX: Math.min(a.maxX, b.maxX),
    maxY: Math.min(a.maxY, b.maxY),
  };
  return out.maxX > out.minX && out.maxY > out.minY ? out : null;
}
